import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Package, Snowflake, Utensils } from "lucide-react";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";
import WellnessDisclaimer from "../../components/content/WellnessDisclaimer";
import { PHOTO_BRIEF } from "../../lib/contentImagePlaceholders";

const tips = [
  {
    title: "Fresh gel must be refrigerated",
    body: "Treat MossMatrix gel like fresh food. Straight into the fridge when it lands. Do not leave it out at room temperature between uses. The cold keeps texture stable and flavour bright.",
    Icon: Snowflake,
  },
  {
    title: "Keep sealed",
    body: "A tight lid keeps oxygen and stray fridge smells out. Your gel tastes like ours, not last night’s leftovers.",
    Icon: Package,
  },
  {
    title: "Use a clean spoon (no double dipping)",
    body: "Each dip introduces microbes. A dry, clean spoon (and avoiding double dipping) keeps batches happier for longer.",
    Icon: Utensils,
  },
  {
    title: "Avoid leaving out at room temperature",
    body: "Warm counters are when spoilage risk climbs. Back to the fridge between uses.",
    Icon: AlertTriangle,
  },
  {
    title: "Freeze portions if needed",
    body: "Pour flat portions into freezer-safe pouches, tubs, silicone trays, or heavy-duty foil zip bags, and label the date.",
    Icon: Snowflake,
  },
  {
    title: "Do not freeze in glass",
    body: "Liquid expands and glass jars can crack. Decant first, then freeze.",
    Icon: AlertTriangle,
  },
  {
    title: "Powders, pouches, and posting",
    body: "Powdered herbs and dried sea moss in sealed foil zip bags are lighter, often easier to post, and may last longer before you prepare them at home. Useful when you want pantry flexibility alongside fresh gel.",
    Icon: Package,
  },
];

export default function HowToStoreSeaMossGelPage() {
  const path = "/learn/how-to-store-sea-moss-gel";
  return (
    <SiteShell theme="dark">
      <SEO
        title="How to store sea moss gel | Fridge, freezer and jars"
        description="Store MossMatrix sea moss gel refrigerated, sealed, with clean spoons; freeze in pouches or silicone trays, never glass. Gel vs dried sea moss bag comparison."
        path={path}
      />
      <ContentPageLayout
        eyebrow="Learn"
        title="Storage that respects your batch."
        subtitle="You paid for small-batch gel. A few fridge habits keep it tasting the way we bottled it."
      >
        <div className="grid md:grid-cols-2 gap-5 mb-12">
          {tips.map((t) => {
            const Ico = t.Icon;
            return (
              <div key={t.title} className="rounded-2xl border border-cream bg-white p-6">
                <h2 className="font-serif text-xl text-forest flex items-start gap-2.5">
                  <Ico className="w-5 h-5 text-gold/90 shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden />
                  <span>{t.title}</span>
                </h2>
                <p className="mt-3 text-sm text-botanical/85 leading-relaxed">{t.body}</p>
              </div>
            );
          })}
        </div>

        <section className="rounded-3xl border border-cream bg-offwhite p-8 md:p-10 mb-10">
          <h2 className="font-serif text-2xl text-forest flex items-center gap-2">
            <Package className="w-5 h-5 text-gold/90 shrink-0" strokeWidth={1.5} aria-hidden />
            <span>Gel jar or dried sea moss bag?</span>
          </h2>
          <div className="mt-6 grid md:grid-cols-2 gap-6 text-sm text-botanical/85 leading-relaxed">
            <div className="rounded-2xl border border-cream bg-white p-5">
              <h3 className="font-serif text-lg text-forest">Ready-to-use gel</h3>
              <p className="mt-2">
                MossMatrix gel is soaked, blended, and bottled for you. Open the jar and stir into drinks or bowls. Convenience-first; keep it cold.
              </p>
              <p className="mt-3 text-[10px] text-botanical/50">{PHOTO_BRIEF.fridgeStorage}</p>
            </div>
            <div className="rounded-2xl border border-cream bg-white p-5">
              <h3 className="font-serif text-lg text-forest">Dried sea moss in a sealed foil bag</h3>
              <p className="mt-2">
                Dried sea moss is lighter, often easier to post, and may keep longer before you prepare it at home, but you still need to soak, rinse, and blend. Some customers want both: gel for now, dried for travel.
              </p>
              <p className="mt-3 text-[10px] text-botanical/50">{PHOTO_BRIEF.driedFoilBag}</p>
            </div>
          </div>
        </section>

        <WellnessDisclaimer className="max-w-3xl mx-auto mb-10" />

        <ConversionCtas />

        <p className="text-sm text-botanical/80 text-center mt-10">
          <Link to="/learn" className="underline hover:text-forest">
            Learn hub
          </Link>
          {" · "}
          <Link to="/faq" className="underline hover:text-forest">
            FAQ
          </Link>
          {" · "}
          <Link to="/shop" className="underline hover:text-forest">
            Shop
          </Link>
          {" · "}
          <Link to="/shop" className="underline hover:text-forest">Choose your sea moss</Link>{" � "}<Link to="/mix-ins" className="underline hover:text-forest">Explore mix-in ideas</Link>
          {" · "}
          <Link to="/ingredients" className="underline hover:text-forest">
            Ingredients
          </Link>
        </p>
      </ContentPageLayout>
    </SiteShell>
  );
}
